import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagService {

  constructor() { }

  /**
   * Check if payment features are enabled
   */
  isPaymentsEnabled(): boolean {
    return environment.features?.paymentsEnabled ?? true;
  }

  /**
   * Check if Stripe integration is enabled
   */
  isStripeEnabled(): boolean {
    return environment.features?.stripeEnabled ?? true;
  }

  /**
   * Check if running in whitelabel mode
   */
  isWhitelabelMode(): boolean {
    return !this.isPaymentsEnabled();
  }

  /**
   * Check if transactions/billing features should be shown
   */
  isTransactionsEnabled(): boolean {
    return this.isPaymentsEnabled();
  }

  /**
   * Check if bank details/payment setup should be shown
   */
  isBankDetailsEnabled(): boolean {
    return this.isPaymentsEnabled();
  }

  /**
   * Check if payment method selection should be shown in booking flow
   */
  isBookingPaymentEnabled(): boolean {
    return this.isPaymentsEnabled() && this.isStripeEnabled();
  }

  /**
   * Check if payment-related profile sections should be shown
   */
  isPaymentProfileEnabled(): boolean {
    return this.isPaymentsEnabled();
  }

  /**
   * Check if refund functionality should be available
   */
  isRefundEnabled(): boolean {
    return this.isPaymentsEnabled() && this.isStripeEnabled();
  }

  /**
   * Check if earnings/financial reporting should be shown
   */
  isFinancialReportingEnabled(): boolean {
    return this.isPaymentsEnabled();
  }

  /**
   * Check if payment terms should be shown
   */
  isPaymentTermsEnabled(): boolean {
    return this.isPaymentsEnabled();
  }

  /**
   * Check if user is a hospital account
   */
  isHospitalUser(): boolean {
    const userType = localStorage.getItem('endUserType');
    return userType === 'hospital' || userType === 'vc' || userType === 'pcv';
  }

  /**
   * Check if specialty intake forms feature is enabled
   * When enabled, specialties listed in specialtyIntakeForms will load their custom intake forms
   * When disabled, all specialties will use the standard appointment form
   */
  isSpecialtyIntakeEnabled(): boolean {
    return environment.features?.specialtyIntakeEnabled ?? false;
  }

  /**
   * Check if specialty intake form should be shown for a specific specialty
   * This combines:
   * 1. The specialty intake feature flag
   * 2. Hospital user check (for creating forms)
   * 3. The specialty being in the specialtyIntakeForms list (checked at component level)
   *
   * Note: This is used when CREATING appointments. For VIEWING appointments,
   * specialists can see specialty intake data even though they're not hospital users.
   */
  shouldShowSpecialtyIntakeForm(): boolean {
    return this.isSpecialtyIntakeEnabled() && this.isHospitalUser();
  }

  /**
   * Check if specialty intake data should be displayed (for viewing)
   * This is less restrictive than shouldShowSpecialtyIntakeForm() because:
   * - Specialists need to VIEW specialty intake data to consult on cases
   * - Only hospital users can CREATE appointments with specialty intake forms
   */
  shouldDisplaySpecialtyIntakeData(): boolean {
    return this.isSpecialtyIntakeEnabled();
  }

  /**
   * Determine which Out of Office workflow to use
   * Enhanced mode: Whitelabel with specialty intake (conflict detection, no payments)
   * Legacy mode: Payment-enabled mode (simple flow with Stripe refunds)
   * @returns 'enhanced' | 'legacy'
   */
  getOOOWorkflowMode(): 'enhanced' | 'legacy' {
    // Enhanced OOO: Whitelabel mode + Specialty intake + No payments
    if (this.isWhitelabelMode() &&
        this.isSpecialtyIntakeEnabled() &&
        !this.isPaymentsEnabled()) {
      return 'enhanced';
    }

    // Legacy OOO: Standard payment-enabled mode
    return 'legacy';
  }

  /**
   * Check if enhanced OOO with conflict detection should be used
   * @returns boolean
   */
  useEnhancedOOO(): boolean {
    return this.getOOOWorkflowMode() === 'enhanced';
  }

  /**
   * Get payment disabled message for whitelabel
   */
  getPaymentDisabledMessage(): string {
    return 'Payment features are not available in this version of the application.';
  }

  /**
   * Get display name for modality based on specialty intake feature
   * When specialty intake is enabled, 'chat' displays as 'Standard'
   * @param modality - The modality value ('chat', 'audio/video', etc.)
   * @returns Display string for the modality
   */
  getModalityDisplayName(modality: string): string {
    if (!modality) return '';

    // When specialty intake is enabled, display 'chat' as 'Standard'
    if (this.isSpecialtyIntakeEnabled() && modality.toLowerCase() === 'chat') {
      return 'Standard';
    }

    // For other modalities, capitalize first letter
    return modality.charAt(0).toUpperCase() + modality.slice(1);
  }

  /**
   * Log feature flag usage for debugging
   */
  logFeatureUsage(featureName: string, enabled: boolean): void {
    if (!environment.production) {
    }
  }
}